import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TIssueWorklog,
  TIssueWorklogCreate,
  TIssueWorklogUpdate,
  TIssueWorklogTotal,
  TProjectWorklogSummary,
  TProjectEconomics,
  TTemplateCostEstimate,
  TTeamHourlyRate,
} from "@/types/worklog";

/** Must not exceed the endpoint's own `MAX_WORKSPACE_WORKLOG_SUMMARY_IDS` (250). */
const WORKSPACE_WORKLOG_SUMMARY_CHUNK = 250;

export class WorklogService extends ApiService {
  async listIssueWorklogs(
    slug: string,
    projectId: string,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueWorklog[]> {
    // Server-paginated (issue #1254); walk the pages so the worklog panel keeps
    // the full set it renders locally. UI is unchanged.
    return this.getAllPages<TIssueWorklog>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `/api/workspaces/${slug}/projects/${projectId}/issues/${issueId}/worklogs/?${qs}`;
      },
      undefined,
      config,
    );
  }

  async createIssueWorklog(
    slug: string,
    projectId: string,
    issueId: string,
    data: TIssueWorklogCreate,
  ): Promise<TIssueWorklog> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/issues/${issueId}/worklogs/`,
      data,
    );
  }

  async updateIssueWorklog(
    slug: string,
    projectId: string,
    issueId: string,
    worklogId: string,
    data: TIssueWorklogUpdate,
  ): Promise<TIssueWorklog> {
    return this.patch(
      `/api/workspaces/${slug}/projects/${projectId}/issues/${issueId}/worklogs/${worklogId}/`,
      data,
    );
  }

  async deleteIssueWorklog(
    slug: string,
    projectId: string,
    issueId: string,
    worklogId: string,
  ): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/issues/${issueId}/worklogs/${worklogId}/`,
    );
  }

  async getIssueWorklogTotal(
    slug: string,
    projectId: string,
    issueId: string,
  ): Promise<TIssueWorklogTotal> {
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/issues/${issueId}/total-worklogs/`,
    );
  }

  async getProjectWorklogSummary(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectWorklogSummary[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/worklogs/`, config);
  }

  /**
   * Logged seconds per work item for an explicit id set, across the whole
   * workspace — the cross-project counterpart to
   * {@link getProjectWorklogSummary}, which a list spanning projects (the
   * workspace Tasks list, My Work) would otherwise have to call once per project
   * on the page.
   *
   * Returns the same `{issue_id, duration}` rows as the project summary, and the
   * same way: an issue with no logged time is **absent**, not a zero row.
   *
   * The endpoint caps `issue_ids` at 250 per request, so a longer page is split
   * and the parts concatenated — callers pass their whole page and don't chunk.
   */
  async getWorkspaceWorklogSummary(
    slug: string,
    issueIds: string[],
    config?: ApiRequestConfig,
  ): Promise<TProjectWorklogSummary[]> {
    const requests: Promise<TProjectWorklogSummary[]>[] = [];
    for (let i = 0; i < issueIds.length; i += WORKSPACE_WORKLOG_SUMMARY_CHUNK) {
      const chunk = issueIds.slice(i, i + WORKSPACE_WORKLOG_SUMMARY_CHUNK);
      requests.push(
        this.get<TProjectWorklogSummary[]>(
          `/api/workspaces/${slug}/worklogs/summary/?${new URLSearchParams({
            issue_ids: chunk.join(","),
          })}`,
          config,
        ),
      );
    }
    // Issued together; `Promise.all` preserves order, so the concatenation is
    // in the same order as the ids came in.
    return (await Promise.all(requests)).flat();
  }

  async getProjectCostSummary(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectEconomics> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/cost-summary/`, config);
  }

  // Estimated scope cost of a project template, for the intake Plan Work phase
  // (before a project exists to price). Gated on the admin-only financialData.view,
  // same as the project cost surfaces.
  //
  // `memberIds` is the planner's current team selection: each template row's hours
  // are priced from a staffed member holding that row's job role, falling back to
  // the role's own rate where nobody is staffed on it yet. Omitting it (or passing
  // an empty selection) yields the pure role-rate estimate.
  //
  // `plannedHours` is the planner's own effort figure. Given, the template's role
  // mix is scaled to it so editing effort moves the cost; omitted, the estimate
  // stays on the template's own hours.
  async getProjectTemplateCostEstimate(
    slug: string,
    projectTemplateId: string,
    memberIds: string[] = [],
    plannedHours?: number,
  ): Promise<TTemplateCostEstimate> {
    const params = new URLSearchParams();
    if (memberIds.length > 0) params.set("member_ids", memberIds.join(","));
    // Only a usable figure is sent; the backend treats anything else as absent,
    // but there is no reason to put a NaN on the wire in the first place.
    if (plannedHours != null && Number.isFinite(plannedHours) && plannedHours >= 0) {
      params.set("planned_hours", String(plannedHours));
    }
    const query = params.size > 0 ? `?${params.toString()}` : "";
    return this.get(
      `/api/workspaces/${slug}/project-templates/${projectTemplateId}/cost-estimate/${query}`,
    );
  }

  // A team's combined hourly rate (sum of members' current rates). Gated on the
  // admin-only financialData.view, same as the project cost surface.
  async getTeamHourlyRate(
    slug: string,
    teamId: string,
    config?: ApiRequestConfig,
  ): Promise<TTeamHourlyRate> {
    return this.get(`/api/workspaces/${slug}/teams/${teamId}/hourly-rate/`, config);
  }
}
